import { Web3Utils } from "utils/web3-utils";
import { getCalculatedFields } from "./utils";
import { BasePool, Pool, PoolVersionType } from "types";
import { PoolsCache } from "utils/antepools/cache";
import { Ante } from "@antefinance/ante-sdk";
import { Wallet } from "utils/wallet";
import { PoolVersionTypeMapping } from "@antefinance/ante-sdk/dist/ante";
import fetchOnChainPoolV05 from "./v0.5/fetchOnChainPool";
import fetchOnChainPoolV052 from "./v0.5.2/fetchOnChainPool";
import fetchOnChainPoolV06 from "./v0.6/fetchOnChainPool";
import { getNetworkAliasById } from "utils/utils";
import queryClient from "utils/queryClient";
import { TestsCache } from "utils/ante-tests/cache";
import { CHAINS } from "utils/constants";
import { JsonRpcProvider } from "@ethersproject/providers";

export type SendTransaction = (
  method: () => Promise<any>,
  toastMsg: string,
  analyticsProps: any
) => void;

export const fetchOnChainPoolData = async (
  walletData: Partial<Wallet>,
  pool: Pool
): Promise<Pool> => {
  // If user is connected to a network other than the pool network,
  // ignore fetching on chain data
  return !pool.chainId ||
    walletData.networkId.toLowerCase() !== pool.chainId.toLowerCase()
    ? pool
    : await fetchPool(pool, walletData);
};

export const fetchOnChainPoolsData = async (
  walletData: Partial<Wallet>,
  pools: Pool[]
): Promise<Pool[]> => {
  return await Promise.all(
    pools?.map(async (pool: Pool) => {
      return await fetchOnChainPoolData(walletData, pool);
    })
  );
};

type PoolFetchers = {
  [key in PoolVersionType]?: (
    offChainPool: BasePool,
    publicPoolInstance: PoolVersionTypeMapping[key],
    poolInstance: PoolVersionTypeMapping[key],
    sendTransaction: SendTransaction,
    account: string
  ) => Promise<Record<string, any>>;
};

const fetchers: PoolFetchers = {
  "v0.5": fetchOnChainPoolV05,
  "v0.5.1": fetchOnChainPoolV05,
  "v0.5.2": fetchOnChainPoolV052,
  "v0.6": fetchOnChainPoolV06,
};

const PROVIDERS = new Map<string, JsonRpcProvider>();

const getProvider = (networkId: string): JsonRpcProvider | undefined => {
  if (!PROVIDERS.has(networkId)) {
    const chain = Object.values(CHAINS).find((chain) => chain.id === networkId);
    if (!chain) {
      return undefined;
    }
    PROVIDERS.set(networkId, new JsonRpcProvider(chain.rpcUrl));
  }
  return PROVIDERS.get(networkId)!;
};

const ANTE_INSTANCES = new Map<string, Ante>();
const getAnteInstance = (networkId: string, provider: JsonRpcProvider) => {
  if (!ANTE_INSTANCES.has(networkId)) {
    ANTE_INSTANCES.set(
      networkId,
      new Ante(getNetworkAliasById(networkId), provider)
    );
  }
  return ANTE_INSTANCES.get(networkId)!;
};

const PUBLIC_ANTE_INSTANCES = new Map<string, Ante>();
const getPublicAnteInstance = (
  networkId: string,
  provider: JsonRpcProvider
) => {
  if (!PUBLIC_ANTE_INSTANCES.has(networkId)) {
    PUBLIC_ANTE_INSTANCES.set(
      networkId,
      new Ante(getNetworkAliasById(networkId), provider)
    );
  }
  return PUBLIC_ANTE_INSTANCES.get(networkId)!;
};

export const fetchPool = async (
  pool: Pool,
  walletData: Partial<Wallet>
): Promise<Pool> => {
  const { provider, networkId, account, refreshBalance } = walletData;

  const ante: Ante = getAnteInstance(networkId, provider);
  const publicAnte: Ante = getPublicAnteInstance(
    networkId,
    getProvider(networkId)
  );

  const sendTransaction: SendTransaction = async (
    method: any,
    toastMsg: string,
    analyticsProps
  ) => {
    if (!account) {
      return Promise.reject();
    }

    await Web3Utils.send(
      method,
      {
        toastMessage: toastMsg,
        analyticsProperties: analyticsProps,
      },
      ante.getProvider()
    );

    refreshBalance();

    const poolData = await fetchOnChainPoolData(walletData, pool);

    PoolsCache.setPool(poolData, walletData);

    setTimeout(() => {
      queryClient.invalidateQueries({
        queryKey: TestsCache.keys.single(pool.chainId, pool.anteTestAddress),
      });
    }, 5000);
  };

  const poolVersion = (pool.version || "v0.5") as PoolVersionType;

  const executePoolFetcher = <V extends PoolVersionType>(version: V) => {
    const poolInstance = ante.getPool(pool.antePoolAddress, version);
    const publicPoolInstance = publicAnte.getPool(
      pool.antePoolAddress,
      version
    );
    return fetchers[version](
      pool,
      publicPoolInstance,
      poolInstance,
      sendTransaction,
      account
    );
  };

  const poolData = await executePoolFetcher(poolVersion);

  const formattedPool = {
    ...pool,
    ...poolData,
    sendTransaction,
  };

  return {
    ...formattedPool,
    ...getCalculatedFields(formattedPool),
  };
};
